import { isEmpty } from "lodash";
import { Location, type ILocation } from "@domain/General/Location";
import { ConditionType } from "@domain/Vehicle";
import { useRouter } from "@library/Services";
import { ConditionConfiguration } from "./Conditions/ConditionConfiguration";
import type { IAppConfiguration, IAppConfigurationDto } from "./IAppConfiguration";
import type { IConditionConfiguration } from "./Conditions/IConditionConfiguration";
import { useOfferService } from "@app/MyMarketplace/Services/OfferService";
import type { AppType } from ".";
import { getGpsCoordinatesWithTimeout } from "@services/GpsCoordinates";

/**
 * Sets the app configuration
 * @param model 
 * @returns 
 */
export function setAppConfiguration( model: IAppConfigurationDto )
{
    AppConfiguration.instance = new AppConfiguration( model );
    return AppConfiguration.instance;
}

/**
 * Gets or sets the Carbucks app configuration from/in local storage
 * @param model 
 * @returns 
 */
export function useAppConfiguration()
{
    return AppConfiguration.instance!;
}

/**
 * Represents the app configuration settings
 */
export class AppConfiguration implements IAppConfiguration
{ 
    
    appConfigurationId: string;
    name: string;
    brandName: string;
    theme: string;
    isDarkMode: boolean;
    appType: AppType;
    myGarageTitle: string;
    marketplaceTitle: string;
    marketplaceRadiusKm: number;
    safetyCenterTitle: string;

    dealerLocation: ILocation;
    serviceLocation: ILocation;
    conditionConfigurations: IConditionConfiguration[];
    isGeneric: boolean;
    isValid: boolean;
    
    public static instance: IAppConfiguration = {} as IAppConfiguration;

    constructor( model: IAppConfigurationDto, isValid: boolean = true )
    {

        this.appConfigurationId = model.appConfigurationId;
        this.isValid = isValid;
        this.name = model.name;
        this.brandName = model.brandName;
        this.theme = model.theme;
        this.isDarkMode = model.isDarkMode;
        this.appType = model.appType;
        this.myGarageTitle = model.myGarageTitle;
        this.marketplaceTitle = model.marketplaceTitle;
        this.marketplaceRadiusKm = model.marketplaceRadiusKm;
        this.safetyCenterTitle = model.safetyCenterTitle;

        this.dealerLocation = new Location( model.dealerLocation );
        this.serviceLocation = new Location( model.serviceLocation ); 
        this.conditionConfigurations = model.conditionConfigurations?.map( c => new ConditionConfiguration( c ) ) ?? [];
        this.isGeneric = model.isGeneric;

        if( isValid )
        {
            this.updatePageTitles();
        }

    }

    /**
     * Initialises the app configuration
     */
    public async initialise()
    {
        await this.fetchLinkedConditionOffers();
    }   
    
    /**
     * True if there is a service booking url
     */
    public get hasServiceBookingUrl()
    {
        return ( this.getServiceBookingUrl() ?? '' ).trim().length > 0;     
    }

    /**
     * True if the originator provides services (has an offer) for the given condition type
     * @param conditionType 
     * @returns 
     */
    hasOriginatorCapability( conditionType: ConditionType )
    {
        return this.conditionConfigurations.find( c => c.conditionType == conditionType && c.hasOriginatorCapability ) != null;
    }
 
    /**
     * Gets the category configuration for the given condition type
     * @param conditionType 
     * @returns 
     */
    getConditionConfiguration( conditionType: ConditionType )
    {
        return this.conditionConfigurations.find( c => c.conditionType == conditionType )
                ?? ConditionConfiguration.NOT_FOUND( conditionType );
    }
    
    /**
     * Gets the linked offer from the service condition if one has been configured
     */
    getServiceOffer()
    {
        return this.conditionConfigurations.find( c => c.conditionType == ConditionType.Service )?.offer ?? null;
    }

    /**
     * Gets the service booking url from the service offer linkUrl, or the service location linkUrl
     * @returns 
     */
    getServiceBookingUrl()
    {
        return ( isEmpty( this.getServiceOffer()?.linkUrl ) ? null : this.getServiceOffer()?.linkUrl )
            ?? ( isEmpty( this.serviceLocation?.linkUrl ) ? null : this.serviceLocation?.linkUrl )
            ?? null;
    }

    /**
     * Updates the route table with page titles that came from the settings
     */
    private updatePageTitles()
    {

        const router = useRouter();
        const routes = router.vueRouter.getRoutes();

        const myGarageRoute = routes.find( x => x.path == '/my-garage' );
        const myMarketplaceRoute = routes.find( x => x.path == '/my-marketplace' );
        const safetyCenterRoute = routes.find( x => x.path == '/safety' );

        myGarageRoute!.meta.title = this.myGarageTitle;
        myMarketplaceRoute!.meta.title = this.marketplaceTitle;
        safetyCenterRoute!.meta.title = this.safetyCenterTitle;

    }

    /**
     * Fetches any offers that have been linked to conditions from 
     * the backend and attaches them to the condition configuration
     * to prevent them being reloaded on each route change
     */
    private async fetchLinkedConditionOffers()
    {

        const offerService = useOfferService();
        const configurations = this.conditionConfigurations.filter( c => c.offerId != null );
        
        // We need the user's current location in order to complete the filtering on the initial/landing page.
        // If the co-ordinates still haven't been retrieved within three seconds, assume we aren't getting them in order to prevent the page from waiting.
        // The only reason why this timeout should be executed is because the app is asking for permission to get the current location. If it already has permission or if it already has been denied permission, then it will exit/resolve straight away.
        const location = await getGpsCoordinatesWithTimeout( 3000 );

        for( var c in configurations )
        {
            const configuration = configurations[ c ];

            configuration.offer = await offerService.getOffer( configuration.offerId!, location );
        }

    }

}
